/**
 * orchestration-router.ts
 *
 * Capacity-Aware Orchestration Router, Joint Runtime & Node Eligibility,
 * Least-Connections Elastic Needle2 Distribution, Rejection Telemetry,
 * and Hysteresis-Gated Dynamic Scaling Signals.
 */

import {
  RoutingDecision,
  ModelKind,
  AssignmentScope,
  CandidateRejection,
  ModelRuntime,
  NodeCapability,
  ModelAssignment,
  ScaleSignal,
} from '../contracts/model-orchestration-contracts.js';
import { OrchestrationStore } from '../storage/orchestration-store.js';
import { NodeCapabilityRegistry } from '../nodes/node-capability-registry.js';
import { ModelAssignmentStore } from '../assignments/model-assignment-store.js';
import { ScalingTracker, DEFAULT_SCALING_POLICY } from './scaling-policy.js';

export interface RouteRequestOptions {
  readonly requestId?: string;
  readonly scope?: AssignmentScope;
  readonly scopeId?: string;
  readonly requiredCapabilities?: readonly string[];
  readonly dryRun?: boolean;
}

export class OrchestrationRouter {
  private readonly scalingTracker = new ScalingTracker();

  constructor(
    private readonly store: OrchestrationStore,
    private readonly nodeRegistry: NodeCapabilityRegistry,
    private readonly assignmentStore: ModelAssignmentStore
  ) {}

  getScalingTracker(): ScalingTracker {
    return this.scalingTracker;
  }

  /**
   * Evaluates joint eligibility of a runtime instance and its hosting node.
   */
  private evaluateEligibility(
    runtime: ModelRuntime,
    node: NodeCapability,
    assignment?: ModelAssignment,
    now: number = Date.now()
  ): { eligible: boolean; reason?: string } {
    // 1. Node Lifecycle State MUST be ONLINE
    if (node.lifecycleState !== 'ONLINE') {
      return { eligible: false, reason: `Node ${node.nodeId} is not ONLINE (current: ${node.lifecycleState})` };
    }

    // 2. Node Administrative Policy
    if (node.policy.disabled) {
      return { eligible: false, reason: `Node ${node.nodeId} is administratively disabled` };
    }
    if (node.policy.quarantined) {
      return { eligible: false, reason: `Node ${node.nodeId} is in security quarantine` };
    }
    if (node.policy.draining) {
      return { eligible: false, reason: `Node ${node.nodeId} is draining workloads` };
    }

    // 3. Runtime Instance Status MUST be active
    if (runtime.status !== 'active') {
      return { eligible: false, reason: `Runtime ${runtime.runtimeId} is not active (current: ${runtime.status})` };
    }

    // 4. Effective Concurrency with Recovery Ramp-Up
    const rampMultiplier = this.nodeRegistry.getCapacityMultiplier(node, now);
    const effectiveMaxConcurrency = Math.max(1, Math.floor(runtime.maxConcurrentRequests * rampMultiplier));

    if (runtime.activeRequests >= effectiveMaxConcurrency) {
      return {
        eligible: false,
        reason: `Runtime ${runtime.runtimeId} is at capacity (${runtime.activeRequests}/${effectiveMaxConcurrency})`,
      };
    }

    // 5. Capability Match
    if (assignment && assignment.requiredCapabilities.length > 0) {
      const nodeInstalled = node.installedModelIds;
      if (!nodeInstalled.includes(runtime.modelId)) {
        return { eligible: false, reason: `Model ${runtime.modelId} not installed on node ${node.nodeId}` };
      }
    }

    return { eligible: true };
  }

  /**
   * Computes deterministic candidate score.
   * Higher score is preferred.
   */
  private calculateCandidateScore(
    runtime: ModelRuntime,
    node: NodeCapability,
    assignment?: ModelAssignment
  ): number {
    let score = 1000;

    // Assignment Priority
    if (assignment) {
      score += assignment.priority * 10;
      if (assignment.preferredNodeId === node.nodeId) score += 500;
      if (assignment.preferredModelId === runtime.modelId) score += 500;
    }

    // Least-connections load bonus (fewer active requests = higher score)
    const loadFactor = Math.max(0, 10 - runtime.activeRequests);
    score += loadFactor * 50;

    // Node Headroom
    const availableMemRatio = node.capacity.availableMemoryMb / Math.max(1, node.capacity.totalMemoryMb);
    score += availableMemRatio * 100;

    return score;
  }

  /**
   * Primary route resolution engine for Needle2 and LLM requests.
   */
  async route(
    modelKind: ModelKind,
    options: RouteRequestOptions = {},
    now: number = Date.now()
  ): Promise<RoutingDecision> {
    const requestId = options.requestId || `req-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const scope = options.scope || 'global';
    const scopeId = options.scopeId || '__global__';

    // 1. Resolve Assignment
    const assignment = await this.assignmentStore.resolveAssignment(scope, scopeId, modelKind);

    // 2. Fetch all candidate runtimes and nodes
    const allRuntimes = await this.store.getRuntimes();
    const kindRuntimes = allRuntimes.filter((r) => r.kind === modelKind);

    const allNodes = await this.store.getNodeCapabilities();
    const nodeMap = new Map(allNodes.map((n) => [n.nodeId, n]));

    const eligibleCandidates: { runtime: ModelRuntime; node: NodeCapability; score: number }[] = [];
    const rejectionReasons: CandidateRejection[] = [];

    for (const runtime of kindRuntimes) {
      const node = nodeMap.get(runtime.nodeId);
      if (!node) {
        rejectionReasons.push({
          nodeId: runtime.nodeId,
          runtimeId: runtime.runtimeId,
          reason: `Node ${runtime.nodeId} not found in capability registry`,
        });
        continue;
      }

      const eligibility = this.evaluateEligibility(runtime, node, assignment, now);
      if (eligibility.eligible) {
        const score = this.calculateCandidateScore(runtime, node, assignment);
        eligibleCandidates.push({ runtime, node, score });
      } else {
        rejectionReasons.push({
          nodeId: node.nodeId,
          runtimeId: runtime.runtimeId,
          reason: eligibility.reason || 'Ineligible',
        });
      }
    }

    // 3. Fallback Cross-Model Check
    if (eligibleCandidates.length === 0) {
      if (assignment && !assignment.allowCrossModelFallback) {
        // Strict domain boundary: NEVER route Needle2 to LLM or vice-versa
        return {
          requestId,
          modelKind,
          selectedNodeId: '',
          selectedRuntimeId: '',
          instanceIndex: -1,
          score: 0,
          reasons: ['No eligible runtimes available', 'allowCrossModelFallback is false'],
          rejectionReasons,
          fallbackCandidates: [],
          decidedAt: new Date(now).toISOString(),
        };
      }
    }

    // 4. Sort eligible candidates by highest score (least loaded first)
    eligibleCandidates.sort((a, b) => b.score - a.score);

    if (eligibleCandidates.length === 0) {
      return {
        requestId,
        modelKind,
        selectedNodeId: '',
        selectedRuntimeId: '',
        instanceIndex: -1,
        score: 0,
        reasons: ['No eligible runtimes available'],
        rejectionReasons,
        fallbackCandidates: [],
        decidedAt: new Date(now).toISOString(),
      };
    }

    const selected = eligibleCandidates[0];
    const fallbackCandidates = eligibleCandidates.slice(1).map((c) => ({
      nodeId: c.node.nodeId,
      runtimeId: c.runtime.runtimeId,
      score: c.score,
    }));

    // 5. Evaluate Scaling Signal for Needle2 worker pools
    if (modelKind === 'needle2') {
      const nodeWorkers = kindRuntimes.filter((r) => r.nodeId === selected.node.nodeId && r.status === 'active');
      const totalActive = nodeWorkers.reduce((sum, w) => sum + w.activeRequests, 0);
      const totalMax = nodeWorkers.reduce((sum, w) => sum + w.maxConcurrentRequests, 0);
      const utilPercent = totalMax > 0 ? (totalActive / totalMax) * 100 : 0;

      this.scalingTracker.recordUtilization(selected.node.nodeId, selected.runtime.modelId, utilPercent, now);
      const scalingPolicy = assignment?.scalingPolicyOverride
        ? { ...DEFAULT_SCALING_POLICY, ...assignment.scalingPolicyOverride }
        : DEFAULT_SCALING_POLICY;

      const scaleSignal = this.scalingTracker.evaluateScaleSignal(
        selected.node.nodeId,
        selected.runtime.modelId,
        scalingPolicy,
        now
      );

      if (scaleSignal) {
        this.scalingTracker.recordSignalEmitted(selected.node.nodeId, selected.runtime.modelId, now);
        const signalObj: ScaleSignal = {
          signalId: `sig-${Date.now()}`,
          nodeId: selected.node.nodeId,
          modelId: selected.runtime.modelId,
          currentInstances: nodeWorkers.length,
          recommendedInstances: nodeWorkers.length + 1,
          utilizationPercent: utilPercent,
          signal: scaleSignal,
          issuedAt: new Date(now).toISOString(),
        };

        await this.store.appendEvent({
          eventId: signalObj.signalId,
          kind: 'scale.signal.emitted',
          payload: signalObj as any,
          originId: 'router',
          sequence: 0,
          occurredAt: new Date(now).toISOString(),
        });
      }
    }

    return {
      requestId,
      modelKind,
      selectedNodeId: selected.node.nodeId,
      selectedRuntimeId: selected.runtime.runtimeId,
      instanceIndex: selected.runtime.instanceIndex,
      score: selected.score,
      reasons: ['Selected highest scoring candidate (least connections / optimal headroom)'],
      rejectionReasons,
      fallbackCandidates,
      decidedAt: new Date(now).toISOString(),
    };
  }

  /**
   * Dry-run simulation of routing decision without incrementing activeRequests.
   */
  async simulateRouting(
    scope: AssignmentScope,
    scopeId: string,
    modelKind: ModelKind,
    now: number = Date.now()
  ): Promise<RoutingDecision> {
    return this.route(modelKind, { scope, scopeId, dryRun: true }, now);
  }

  async routeNeedle2(options: RouteRequestOptions & { estimatedTokens?: number; estimatedDurationMs?: number } = {}, now: number = Date.now()): Promise<RoutingDecision> {
    return this.route('needle2', options, now);
  }

  async routeLlm(options: RouteRequestOptions & { estimatedTokens?: number; estimatedDurationMs?: number } = {}, now: number = Date.now()): Promise<RoutingDecision> {
    return this.route('llm', options, now);
  }
}
